import type { DualScopeTableName } from '@releve/shared';
import { useDualScopeTypes, type DualScopeType } from '@/hooks/useDualScopeTypes';
import { useTranslation } from '@/hooks/useTranslation';
import { useOrganization } from '@/contexts/OrganizationContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface DualScopeSelectProps {
  /**
   * Name of the types table (e.g., 'asset_types', 'project_types')
   */
  tableName: DualScopeTableName;

  /**
   * Current selected value (type ID)
   */
  value?: string;

  /**
   * Callback when selection changes
   */
  onValueChange?: (value: string) => void;

  /**
   * Placeholder text when no value selected (default: `dualScope.select.placeholder`)
   */
  placeholder?: string;

  /**
   * Whether the select is disabled
   */
  disabled?: boolean;

  /**
   * Whether to only show active types
   */
  activeOnly?: boolean;

  /**
   * Optional custom render for items
   */
  renderItem?: (type: DualScopeType) => React.ReactNode;
}

/**
 * Reusable select component for dual-scope type dropdowns
 *
 * Automatically fetches global + organization-specific types and renders them
 * with visual separation: the standard (global) types first, then the org's own.
 *
 * @example
 * ```tsx
 * <DualScopeSelect
 *   tableName="asset_types"
 *   value={assetTypeId}
 *   onValueChange={(id) => form.setValue('asset_type_id', id)}
 *   placeholder="Select asset type"
 * />
 * ```
 */
export function DualScopeSelect({
  tableName,
  value,
  onValueChange,
  placeholder,
  disabled = false,
  activeOnly = true,
  renderItem,
}: DualScopeSelectProps) {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();

  const { data: types, isLoading, error } = useDualScopeTypes(
    tableName,
    selectedOrganization?.id,
    { activeOnly }
  );

  // Separate global and org-specific types
  const globalTypes = types?.filter((type) => type.is_global) || [];
  const orgTypes = types?.filter((type) => !type.is_global) || [];

  const defaultRenderItem = (type: DualScopeType) => <span>{type.name}</span>;

  const itemRenderer = renderItem || defaultRenderItem;

  // Always render a single, controlled <Select>. Previously a separate,
  // value-less <Select> was rendered while `isLoading`, which flipped Radix
  // from uncontrolled to controlled on the same element once the types
  // resolved — dropping the bound value (the "empty type dropdown when
  // editing" bug) and logging a controlled/uncontrolled console warning.
  return (
    <Select
      value={value ?? ''}
      onValueChange={onValueChange}
      disabled={disabled || isLoading || !!error}
    >
      <SelectTrigger>
        {isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-muted-foreground">{t('dualScope.select.loading')}</span>
          </div>
        ) : error ? (
          <span className="text-destructive">{t('dualScope.select.error')}</span>
        ) : (
          <SelectValue placeholder={placeholder ?? t('dualScope.select.placeholder')} />
        )}
      </SelectTrigger>
      <SelectContent>
        {globalTypes.length > 0 && (
          <SelectGroup>
            {globalTypes.length > 0 && orgTypes.length > 0 && (
              <SelectLabel>{t('dualScope.select.standard')}</SelectLabel>
            )}
            {globalTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {itemRenderer(type)}
              </SelectItem>
            ))}
          </SelectGroup>
        )}

        {globalTypes.length > 0 && orgTypes.length > 0 && <SelectSeparator />}

        {orgTypes.length > 0 && (
          <SelectGroup>
            {globalTypes.length > 0 && (
              <SelectLabel>{t('dualScope.select.custom')}</SelectLabel>
            )}
            {orgTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {itemRenderer(type)}
              </SelectItem>
            ))}
          </SelectGroup>
        )}

        {types && types.length === 0 && (
          <div className="p-2 text-sm text-muted-foreground text-center">
            {t('dualScope.select.none')}
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
